import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/ui/design-system/components/ui/breadcrumb';
import {
    EmptyState,
    EmptyStateContent,
    EmptyStateDescription,
    EmptyStateIcon,
    EmptyStateTitle,
} from '@/ui/design-system/components/ui/empty-state';
import { useMemo, useState } from 'react';
import { Input } from '@/ui/design-system/components/ui/input';
import { Link } from 'react-router-dom';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import { Search } from 'lucide-react';
import Stack from 'components/Stack';
import { removeDiacritics } from 'utils/helpers';
import useCountryParam from 'utils/hooks/useCountryParam';
import { useFetchAffinityRoots } from 'utils/hooks/api';

function RelatedCategoriesSelection() {
    const { mutateToWithCountryParam } = useCountryParam();
    const [searchValue, setSearchValue] = useState('');

    const { data: groupsData, isLoading, isError } = useFetchAffinityRoots();
    const toGroupSlug = (value) =>
        removeDiacritics(String(value || ''))
            .toLowerCase()
            .trim()
            .replace(/[_\s]+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-');

    const normalizedSearch = removeDiacritics(searchValue)
        .replace(/-/g, ' ')
        .toLowerCase()
        .trim();
    const filteredGroups = useMemo(() => {
        if (!groupsData) return [];
        if (!normalizedSearch) return groupsData;

        return groupsData.filter((group) => {
            const groupName = removeDiacritics(group.name || '')
                .replace(/-/g, ' ')
                .toLowerCase()
                .trim();
            return groupName.includes(normalizedSearch);
        });
    }, [groupsData, normalizedSearch]);
    const recommendedGroupNames = useMemo(
        () =>
            new Set([
                'ingredients',
                'benefits',
                'concerns',
                'product format',
                'body parts',
                'beauty trends',
            ]),
        [],
    );
    const { recommendedGroups } = useMemo(() => {
        if (!groupsData) {
            return { recommendedGroups: [] };
        }
        const recommended = [];
        groupsData.forEach((group) => {
            const groupName = removeDiacritics(group.name || '')
                .replace(/-/g, ' ')
                .toLowerCase()
                .trim();
            if (recommendedGroupNames.has(groupName)) {
                recommended.push(group);
            }
        });
        return { recommendedGroups: recommended };
    }, [groupsData, recommendedGroupNames]);
    const sortedGroups = useMemo(() => {
        const nextGroups = [...filteredGroups];
        nextGroups.sort((a, b) => {
            const aLabel = (a.name || a.id || '')
                .replace(/-/g, ' ')
                .toLowerCase();
            const bLabel = (b.name || b.id || '')
                .replace(/-/g, ' ')
                .toLowerCase();
            return aLabel.localeCompare(bLabel);
        });
        return nextGroups;
    }, [filteredGroups]);
    const groupedGroups = useMemo(() => {
        const groupsByLetter = new Map();
        sortedGroups.forEach((group) => {
            const label = (group.name || group.id || '')
                .replace(/-/g, ' ')
                .trim();
            const firstChar = label[0] || '#';
            const letter = /[A-Za-z]/.test(firstChar)
                ? firstChar.toUpperCase()
                : '#';
            if (!groupsByLetter.has(letter)) {
                groupsByLetter.set(letter, []);
            }
            groupsByLetter.get(letter).push(group);
        });
        return Array.from(groupsByLetter.entries()).map(([letter, items]) => {
            const nextItems = [...items];
            nextItems.sort((a, b) => {
                const aCount = a?.children?.length || 0;
                const bCount = b?.children?.length || 0;
                if (bCount !== aCount) return bCount - aCount;
                const aLabel = (a.name || a.id || '')
                    .replace(/-/g, ' ')
                    .toLowerCase();
                const bLabel = (b.name || b.id || '')
                    .replace(/-/g, ' ')
                    .toLowerCase();
                return aLabel.localeCompare(bLabel);
            });
            return {
                letter,
                items: nextItems,
            };
        });
    }, [sortedGroups]);

    if (isLoading) {
        return (
            <PagePadding className="max-w-7xl">
                <PageBar />
                <Stack $space="1.25rem">
                    <EmptyState>
                        <EmptyStateContent>
                            <EmptyStateIcon />
                            <div className="flex flex-col gap-2">
                                <EmptyStateTitle>
                                    Loading categories
                                </EmptyStateTitle>
                                <EmptyStateDescription>
                                    Please wait while we fetch the available
                                    categories.
                                </EmptyStateDescription>
                            </div>
                        </EmptyStateContent>
                    </EmptyState>
                </Stack>
            </PagePadding>
        );
    }

    if (isError || !groupsData) {
        return (
            <PagePadding className="max-w-7xl">
                <PageBar />
                <Stack $space="1.25rem">
                    <EmptyState>
                        <EmptyStateContent>
                            <EmptyStateIcon />
                            <div className="flex flex-col gap-2">
                                <EmptyStateTitle>
                                    Error loading categories
                                </EmptyStateTitle>
                                <EmptyStateDescription>
                                    Could not load related categories. Please
                                    try again later.
                                </EmptyStateDescription>
                            </div>
                        </EmptyStateContent>
                    </EmptyState>
                </Stack>
            </PagePadding>
        );
    }

    return (
        <PagePadding className="max-w-7xl">
            <PageBar />
            <Stack $space="1.5rem">
                <Stack $space="1.5rem">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/">Home</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>
                                    Related Categories
                                </BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    {recommendedGroups.length > 0 && (
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center gap-2">
                                <h2 className="text-base leading-4 font-semibold text-foreground">
                                    Recommended Categories
                                </h2>
                                <span className="text-sm leading-4 text-muted-foreground">
                                    {recommendedGroups.length}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {recommendedGroups.map((group) => {
                                    const groupSlug = toGroupSlug(
                                        group.name || group.id || '',
                                    );
                                    const groupLabel = (
                                        group.name ||
                                        group.id ||
                                        ''
                                    ).replace(/-/g, ' ');
                                    const groupPath = mutateToWithCountryParam(
                                        `/explore/${groupSlug}/trends`,
                                    );
                                    return (
                                        <Link
                                            key={group.id || group.name}
                                            to={groupPath}
                                            className="group flex h-full flex-col gap-1.5 rounded-lg border border-border bg-card p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        >
                                            <div className="truncate text-base font-medium capitalize text-foreground">
                                                {groupLabel || 'Untitled group'}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {group.children?.length || 0}{' '}
                                                Trends
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h2 className="text-base leading-4 font-semibold text-foreground">
                                Related Categories
                            </h2>
                            <span className="text-sm leading-4 text-muted-foreground">
                                {filteredGroups.length}
                            </span>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            type="search"
                            placeholder="Search categories"
                            aria-label="Search categories"
                            value={searchValue}
                            onChange={(event) =>
                                setSearchValue(event.target.value)
                            }
                            className="pl-10 bg-card shadow-sm"
                        />
                    </div>
                </Stack>

                {filteredGroups.length === 0 ? (
                    <EmptyState>
                        <EmptyStateContent>
                            <EmptyStateIcon />
                            <div className="flex flex-col gap-2">
                                <EmptyStateTitle>
                                    No categories found
                                </EmptyStateTitle>
                                <EmptyStateDescription>
                                    Try a different search to find a related
                                    category.
                                </EmptyStateDescription>
                            </div>
                        </EmptyStateContent>
                    </EmptyState>
                ) : (
                    <div className="flex flex-col gap-8">
                        {groupedGroups.map((groupSection) => (
                            <div
                                key={groupSection.letter}
                                className="flex flex-col gap-3"
                            >
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                                    {groupSection.letter}
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                    {groupSection.items.map((group) => {
                                        const groupSlug = toGroupSlug(
                                            group.name || group.id || '',
                                        );
                                        const groupLabel = (
                                            group.name ||
                                            group.id ||
                                            ''
                                        ).replace(/-/g, ' ');
                                        const groupPath =
                                            mutateToWithCountryParam(
                                                `/explore/${groupSlug}/trends`,
                                            );
                                        return (
                                            <Link
                                                key={group.id || group.name}
                                                to={groupPath}
                                                className="group flex h-full flex-col gap-1.5 rounded-lg border border-border bg-card p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            >
                                                <div className="truncate text-base font-medium capitalize text-foreground">
                                                    {groupLabel ||
                                                        'Untitled group'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {group.children?.length ||
                                                        0}{' '}
                                                    Trends
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Stack>
        </PagePadding>
    );
}

export default RelatedCategoriesSelection;
